import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Search, Mail, Phone, Pencil, Trash2, Camera, Hash, User, Lock, UserPlus } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const Collaborators = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [collaborators, setCollaborators] = useState([]);
  const [filteredCollaborators, setFilteredCollaborators] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    photo: '',
    matricule: '',
  });
  const [createUserAccount, setCreateUserAccount] = useState(false);
  const [userPassword, setUserPassword] = useState('');
  const [userRole, setUserRole] = useState('collaborator');

  useEffect(() => {
    loadCollaborators();
  }, []);

  useEffect(() => {
    const filtered = collaborators.filter((collab) =>
      collab.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (collab.email && collab.email.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    setFilteredCollaborators(filtered);
  }, [searchTerm, collaborators]);

  const loadCollaborators = async () => {
    try {
      const { data } = await axios.get(`${API}/collaborators`);
      setCollaborators(data);
      setFilteredCollaborators(data);
    } catch (error) {
      toast.error('Erreur lors du chargement des collaborateurs');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingPhoto(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const { data } = await axios.post(`${API}/collaborators/upload-photo`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setFormData({ ...formData, photo: data.photo });
      toast.success('Photo ajoutée avec succès');
    } catch (error) {
      toast.error('Erreur lors du téléchargement de la photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate user account fields if creating account
    if (createUserAccount && !editingId) {
      if (!formData.email) {
        toast.error('L\'email est requis pour créer un compte utilisateur');
        return;
      }
      if (!userPassword || userPassword.length < 6) {
        toast.error('Le mot de passe doit contenir au moins 6 caractères');
        return;
      }
    }
    
    try {
      let collaboratorId = editingId;
      
      if (editingId) {
        await axios.put(`${API}/collaborators/${editingId}`, formData);
        toast.success('Collaborateur modifié avec succès');
      } else {
        const { data } = await axios.post(`${API}/collaborators`, formData);
        collaboratorId = data.id;
        toast.success('Collaborateur ajouté avec succès');
        
        // Create user account if requested
        if (createUserAccount && formData.email) {
          try {
            await axios.post(`${API}/users`, {
              email: formData.email,
              password: userPassword,
              name: formData.name,
              role: userRole,
              linked_collaborator_id: collaboratorId
            });
            toast.success('Compte utilisateur créé avec succès');
          } catch (userError) {
            toast.error(userError.response?.data?.detail || 'Erreur lors de la création du compte utilisateur');
          }
        }
      }
      setDialogOpen(false);
      resetForm();
      loadCollaborators();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Une erreur est survenue');
    }
  };

  const handleDelete = async (id) => {
    setDeleteId(id);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/collaborators/${deleteId}`);
      toast.success('Collaborateur supprimé avec succès');
      loadCollaborators();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setConfirmOpen(false);
      setDeleteId(null);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', email: '', phone: '', photo: '', matricule: '' });
    setEditingId(null);
    setCreateUserAccount(false);
    setUserPassword('');
    setUserRole('collaborator');
  };

  const openEditDialog = (collab) => {
    setFormData({
      name: collab.name,
      email: collab.email || '',
      phone: collab.phone || '',
      photo: collab.photo || '',
      matricule: collab.matricule || '',
    });
    setEditingId(collab.id);
    setDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-lg font-medium text-muted-foreground">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-heading font-bold tracking-tight leading-none">
            Collaborateurs
          </h1>
          <p className="text-muted-foreground mt-2">
            Gérez vos collaborateurs et leurs informations
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button
              data-testid="add-collaborator-button"
              className="rounded-full shadow-lg shadow-primary/20"
            >
              <Plus className="w-4 h-4 mr-2" />
              Ajouter
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading">
                {editingId ? 'Modifier' : 'Nouveau'} Collaborateur
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Photo</Label>
                <div className="flex flex-col items-center gap-3">
                  <Avatar className="h-24 w-24">
                    {formData.photo ? (
                      <AvatarImage src={formData.photo} alt="Aperçu" />
                    ) : null}
                    <AvatarFallback className="bg-primary text-primary-foreground text-2xl">
                      {formData.name ? formData.name.charAt(0).toUpperCase() : '?'}
                    </AvatarFallback>
                  </Avatar>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handlePhotoUpload}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingPhoto}
                    className="rounded-full"
                  >
                    <Camera className="w-4 h-4 mr-2" />
                    {uploadingPhoto ? 'Traitement...' : 'Ajouter une photo'}
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="name">Nom complet *</Label>
                <Input
                  id="name"
                  data-testid="collaborator-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="rounded-lg"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  data-testid="collaborator-email-input"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="rounded-lg"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Téléphone</Label>
                <Input
                  id="phone"
                  data-testid="collaborator-phone-input"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="rounded-lg"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="matricule">Numéro de matricule</Label>
                <div className="relative">
                  <Hash className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="matricule"
                    data-testid="collaborator-matricule-input"
                    value={formData.matricule}
                    onChange={(e) => setFormData({ ...formData, matricule: e.target.value })}
                    placeholder="Ex: EMP001"
                    className="pl-10 rounded-lg"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Ce numéro sera utilisé pour la connexion par QR Code
                </p>
              </div>
              
              {/* User Account Section - Only for new collaborators */}
              {!editingId && (
                <div className="space-y-4 pt-4 border-t">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <UserPlus className="w-4 h-4 text-primary" />
                      <Label htmlFor="create-account" className="font-medium">
                        Créer un compte utilisateur
                      </Label>
                    </div>
                    <Switch
                      id="create-account"
                      checked={createUserAccount}
                      onCheckedChange={setCreateUserAccount}
                    />
                  </div>
                  
                  {createUserAccount && (
                    <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
                      <div className="space-y-2">
                        <Label htmlFor="user-password">Mot de passe *</Label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                          <Input
                            id="user-password"
                            type="password"
                            value={userPassword}
                            onChange={(e) => setUserPassword(e.target.value)}
                            placeholder="Minimum 6 caractères"
                            className="pl-10 rounded-lg"
                            required={createUserAccount}
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="user-role">Rôle</Label>
                        <Select value={userRole} onValueChange={setUserRole}>
                          <SelectTrigger className="rounded-lg">
                            <SelectValue placeholder="Sélectionner un rôle" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="collaborator">Collaborateur</SelectItem>
                            <SelectItem value="manager">Manager</SelectItem>
                            <SelectItem value="admin">Administrateur</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        L'email du collaborateur sera utilisé comme identifiant de connexion
                      </p>
                    </div>
                  )}
                </div>
              )}
              
              <Button
                data-testid="collaborator-submit-button"
                type="submit"
                className="w-full rounded-full"
              >
                {editingId ? 'Modifier' : 'Ajouter'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
        <Input
          data-testid="collaborator-search-input"
          placeholder="Rechercher un collaborateur..."
          className="pl-10 rounded-lg max-w-md"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredCollaborators.map((collab) => (
          <Card
            key={collab.id}
            data-testid={`collaborator-card-${collab.id}`}
            className="hover:shadow-lg transition-shadow duration-200 cursor-pointer"
            onClick={() => navigate(`/collaborators/${collab.id}`)}
          >
            <CardHeader className="pb-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <Avatar className="h-12 w-12">
                    {collab.photo ? (
                      <AvatarImage src={collab.photo} alt={collab.name} />
                    ) : null}
                    <AvatarFallback className="bg-primary text-primary-foreground text-lg">
                      {collab.name.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <CardTitle className="text-lg font-heading">{collab.name}</CardTitle>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {collab.email && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Mail className="w-4 h-4" />
                  <span className="truncate">{collab.email}</span>
                </div>
              )}
              {collab.phone && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Phone className="w-4 h-4" />
                  <span>{collab.phone}</span>
                </div>
              )}
              {collab.matricule && (
                <div className="flex items-center gap-2 text-sm">
                  <Hash className="w-4 h-4 text-primary" />
                  <Badge variant="secondary" className="font-mono">{collab.matricule}</Badge>
                </div>
              )}
              {user?.role === 'admin' && (
                <div className="flex gap-2 pt-2">
                  <Button
                    data-testid={`edit-collaborator-${collab.id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      openEditDialog(collab);
                    }}
                    variant="outline"
                    size="sm"
                    className="flex-1 rounded-full"
                  >
                    <Pencil className="w-3 h-3 mr-1" />
                    Modifier
                  </Button>
                  <Button
                    data-testid={`delete-collaborator-${collab.id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(collab.id);
                    }}
                    variant="destructive"
                    size="sm"
                    className="rounded-full"
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredCollaborators.length === 0 && (
        <div className="text-center py-12">
          <p className="text-muted-foreground">Aucun collaborateur trouvé</p>
        </div>
      )}

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        onConfirm={confirmDelete}
        title="Supprimer le collaborateur"
        description="Êtes-vous sûr de vouloir supprimer ce collaborateur ? Cette action est irréversible."
      />
    </div>
  );
};

export default Collaborators;